import { SalesTypeCodeEnum, StoreAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums';
/**
 * Availability utility.
 */
export default class AvailabilityUtility {
    /**
     * Returns the availability status for the supplied product(s).
     *
     * There are four possible cases for availability.
     * Only one product exists.
     * Several storeAvailabilities exists, where the type of the storeAvailabilities is the same.
     * Several storeAvailabilities exists, where the type of the storeAvailabilities is different.
     * Null, if something unexpected occurs.
     *
     * @param availabilities Store availability information for individual products.
     */
    static getStoreAvailabilityStatus(availabilities) {
        if (availabilities.length === 1) {
            return this.getSingleAvailability(availabilities[0]);
        }
        const storeAvailabilities = this.mapAvailabiltiesToEnum(availabilities);
        if (storeAvailabilities.length === 0) {
            return null;
        }
        const findStoreAvailabilityWithMatchingTypes = this.findStoreAvailabilityWithMatchingTypes(storeAvailabilities);
        if (findStoreAvailabilityWithMatchingTypes) {
            return findStoreAvailabilityWithMatchingTypes;
        }
        const findAnyStoreAvailabilityType = this.findAnyStoreAvailabilityType(storeAvailabilities);
        if (findAnyStoreAvailabilityType) {
            return findAnyStoreAvailabilityType;
        }
        return null;
    }
    /**
     * Returns status indicator color.
     *
     * @param storeAvailability Store availability.
     * @returns Status indicator color.
     */
    static getStoreAvailabilityStatusColor(storeAvailability) {
        const availability = this.getStoreAvailabilty(storeAvailability);
        if (availability) {
            return this.getStoreAvailabilityColor(availability);
        }
        return null;
    }
    /**
     * Returns status indicator color for a given availability status.
     *
     * @param availabilityStatus ...
     * @returns Status indicator color.
     */
    static getStoreAvailabilityColor(availabilityStatus) {
        switch (availabilityStatus) {
            case StoreAvailabilityProbabilityEnum.highInStock:
                return 'green';
            case StoreAvailabilityProbabilityEnum.mediumInStock:
            case StoreAvailabilityProbabilityEnum.lowInStock:
            case StoreAvailabilityProbabilityEnum.someLowOrOutOfStock:
                return 'orange';
            case StoreAvailabilityProbabilityEnum.outOfStock:
            case StoreAvailabilityProbabilityEnum.notSoldAtStore:
                return 'red';
            case StoreAvailabilityProbabilityEnum.orderItemSingle:
            case StoreAvailabilityProbabilityEnum.orderItemMultiple:
                return 'grey';
            default:
                return null;
        }
    }
    static getStoreAvailabilityForProduct(options) {
        const { product, storeAvailabilities } = options;
        if (!product.content || !product.valid || !storeAvailabilities) {
            return null;
        }
        return (storeAvailabilities.find((storeAvailability) => {
            return (product.content?.itemNoLocal === storeAvailability.content.itemNo ||
                product.content?.itemNoGlobal === storeAvailability.content.itemNo);
        }) || null);
    }
    /**
     * Returns status indicator label.
     *
     * @param options Options.
     * @param options.translations Localized information.
     * @param options.storeAvailability Store availability.
     * @returns Status indicator label.
     */
    static getStoreAvailabilityLabel(options) {
        const status = this.getStoreAvailabilty(options.storeAvailability);
        return AvailabilityUtility.getStoreAvailabilityTranslation(status, options.translations);
    }
    /**
     * Returns the store availability translation.
     *
     * @param availabilityStatus ...
     * @param translations ...
     * @returns Store availability translation string.
     */
    static getStoreAvailabilityTranslation(availabilityStatus, translations) {
        switch (availabilityStatus) {
            case StoreAvailabilityProbabilityEnum.highInStock:
                return translations.storeAvailabilityInStock;
            case StoreAvailabilityProbabilityEnum.someLowOrOutOfStock:
                return translations.storeAvailabilitySomeLowOrOutOfStock;
            case StoreAvailabilityProbabilityEnum.mediumInStock:
            case StoreAvailabilityProbabilityEnum.lowInStock:
                return translations.storeAvailabilityLowInStock;
            case StoreAvailabilityProbabilityEnum.outOfStock:
                return translations.storeAvailabilityOutOfStock;
            case StoreAvailabilityProbabilityEnum.notSoldAtStore:
                return translations.storeAvailabilityNotSoldAtStore;
            case StoreAvailabilityProbabilityEnum.orderItemSingle:
            case StoreAvailabilityProbabilityEnum.orderItemMultiple:
                return translations.storeAvailabilityOrderItems;
            default:
                return null;
        }
    }
    /**
     * Returns true if any of the provided availabilities require contacting
     * staff. Otherwise returns false. If an item has multiple sales locations all
     * of them must be "contactStaff" for it to be considered full serve.
     *
     * @param availabilities Store availability information for individual
     * products.
     */
    static hasAnyFullServeAvailability(availabilities) {
        return availabilities.some((availability) => availability.content.cashCarry?.salesLocations?.every(this.salesLocationIsFullServe));
    }
    static salesLocationIsFullServe(salesLocation) {
        const isContactStaff = salesLocation.type === SalesTypeCodeEnum.contactStaff;
        const isMissingLocation = salesLocation.type === SalesTypeCodeEnum.department && !salesLocation.departmentName;
        return isContactStaff || isMissingLocation;
    }
    static getSingleAvailability(storeAvailability) {
        if (storeAvailability.content.cashCarry?.inRange === false) {
            if (storeAvailability.content.homeDelivery?.inRange === true) {
                return StoreAvailabilityProbabilityEnum.orderItemSingle;
            }
            return StoreAvailabilityProbabilityEnum.notSoldAtStore;
        }
        return storeAvailability.content?.cashCarry?.messageType ?? null;
    }
    /**
     * Returns status indicator enum.
     *
     * @param storeAvailability Store availability.
     * @returns StoreAvailabilityProbabilityEnum | null.
     */
    static getStoreAvailabilty(storeAvailability // Prop name and type not to introduce a breaking change from originally only supporting one availabiltity
    ) {
        if (Array.isArray(storeAvailability)) {
            return this.getStoreAvailabilityStatus(storeAvailability);
        }
        return this.getStoreAvailabilityStatus([storeAvailability]);
    }
    /**
     * Maps the data returned from DEXF to our StoreAvailabilityProbabilityEnum[].
     * Special business cases are also handled with this function.
     *
     * @param availabilities IStoreAvailability[].
     */
    static mapAvailabiltiesToEnum(availabilities) {
        const { notSoldAtStore } = StoreAvailabilityProbabilityEnum;
        return availabilities
            .map((storeAvailability) => {
            if (storeAvailability.content.cashCarry?.inRange === false) {
                if (storeAvailability.content.homeDelivery?.inRange === true) {
                    return StoreAvailabilityProbabilityEnum.orderItemSingle;
                }
                return notSoldAtStore;
            }
            return storeAvailability.content.cashCarry?.messageType;
        })
            .filter((type) => {
            return type !== undefined && Object.values(StoreAvailabilityProbabilityEnum).includes(type);
        });
    }
    /**
     * Returns the matching StoreAvailabilityProbabilityEnum, see StoreAvailability flowchart for a clearer picture.
     * Https://i-p-e-x.atlassian.net/wiki/spaces/KOMPIS/pages/36238426201/Component+flow+charts#Store-Availability.
     *
     * @param storeAvailabilities StoreAvailabilityProbabilityEnum[].
     */
    static findStoreAvailabilityWithMatchingTypes(storeAvailabilities) {
        const { highInStock, mediumInStock, lowInStock, outOfStock, notSoldAtStore, orderItemSingle, orderItemMultiple } = StoreAvailabilityProbabilityEnum;
        if (storeAvailabilities.every((type) => type === orderItemSingle)) {
            return orderItemMultiple;
        }
        else if (storeAvailabilities.every((type) => type === notSoldAtStore)) {
            return notSoldAtStore;
        }
        else if (storeAvailabilities.every((type) => type === outOfStock || type === notSoldAtStore)) {
            return outOfStock;
        }
        else if (storeAvailabilities.some((type) => type === orderItemSingle)) {
            return orderItemMultiple;
        }
        else if (storeAvailabilities.every((type) => type === lowInStock || type === mediumInStock)) {
            return lowInStock;
        }
        else if (storeAvailabilities.every((type) => type === highInStock)) {
            return highInStock;
        }
        return null;
    }
    /**
     * Returns the matching StoreAvailabilityProbabilityEnum, see StoreAvailability flowchart for a clearer picture.
     * Https://i-p-e-x.atlassian.net/wiki/spaces/KOMPIS/pages/36238426201/Component+flow+charts#Store-Availability.
     *
     * @param storeAvailabilities StoreAvailabilityProbabilityEnum[].
     */
    static findAnyStoreAvailabilityType(storeAvailabilities) {
        if (storeAvailabilities.some((type) => type === StoreAvailabilityProbabilityEnum.lowInStock ||
            type === StoreAvailabilityProbabilityEnum.outOfStock ||
            type === StoreAvailabilityProbabilityEnum.notSoldAtStore ||
            type === StoreAvailabilityProbabilityEnum.mediumInStock)) {
            return StoreAvailabilityProbabilityEnum.someLowOrOutOfStock;
        }
        return null;
    }
}
//# sourceMappingURL=AvailabilityUtility.js.map